import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import {
    Button,
    Card,
    CardActionArea,
    CardContent,
    Typography,
} from "@mui/material";
import React from "react";
import { CircleFlag } from "../../common/CircleFlag";
import { useFixtures } from "../../common/contexts/fixturesContext";
import { palette } from "../../styles/palette";
import { Bet } from "../../types/Bet";
import {
    POINTS_EXACT,
    POINTS_OUTCOME,
    betOutcomeText,
} from "../../utils/betScoring";
import { getMatchBettingState } from "../../utils/betting";
import CountryDict from "../../utils/countryDict";
import { getDateFromDate, getLocalTimeFromDate } from "../../utils/timeUtils";
import BetOutcomeIcon from "../components/BetOutcomeIcon";
import "./MobileBets.scss";

interface BetCardProps {
  bet: Bet;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentBet: React.Dispatch<React.SetStateAction<Bet>>;
  isDesktop?: boolean;
}

const BetCard: React.FC<BetCardProps> = ({
  bet,
  setIsOpen,
  setCurrentBet,
  isDesktop,
}) => {
  function handleClick(bet: Bet) {
    setCurrentBet(bet);
    setIsOpen(true);
  }
  const { matches } = useFixtures();
  const currentMatch = matches.filter((match) => match.id === bet.matchId)[0];
  // A bet can reference a match that isn't in the loaded set (dropped for missing
  // teams, or not yet fetched). Skip rendering rather than crashing on its fields.
  if (!currentMatch) return null;
  const bettingState = getMatchBettingState(currentMatch);
  const isBettable = bettingState === "open";
  const isLocked = bettingState === "locked";
  const matchAlreadyPlayed = bettingState === "played";
  const isGroupStage = currentMatch.matchType.type === "Group";

  const stageLabel = isGroupStage
    ? `${currentMatch.matchType.type} ${currentMatch.matchType.value}`
    : currentMatch.matchType.value;

  return (
    <Card
      className={`betCard__root${isDesktop ? "" : " betCard__root--mobile"}`}
    >
      <CardActionArea component="div" disableRipple>
        <CardContent className="betCard__content">
          <div className="betCard__headerRow">
            <span className="betCard__stage">{stageLabel}</span>
            <span className="betCard__datetime">
              <ScheduleRoundedIcon />
              {getDateFromDate(currentMatch.kickOffTime?.dateTime || "")}{" "}
              {getLocalTimeFromDate(currentMatch.kickOffTime?.dateTime || "")}
            </span>
          </div>

          <div className="betCard__scoreRow">
            <div className="betCard__team">
              <span className="betCard__flag">
                <CircleFlag
                  countryCode={
                    CountryDict.get(
                      currentMatch.homeTeam.internationalName
                    ) || "Poland"
                  }
                  height={48}
                  width={48}
                />
              </span>
              <span className="betCard__teamName">
                {currentMatch.homeTeam.internationalName}
              </span>
            </div>

            <div className="betCard__score">
              <span className="betCard__scoreNum">{bet.homeTeamScoreBet}</span>
              <span className="betCard__scoreSep">:</span>
              <span className="betCard__scoreNum">{bet.awayTeamScoreBet}</span>
            </div>

            <div className="betCard__team">
              <span className="betCard__flag">
                <CircleFlag
                  countryCode={
                    CountryDict.get(
                      currentMatch.awayTeam.internationalName
                    ) || "Poland"
                  }
                  height={48}
                  width={48}
                />
              </span>
              <span className="betCard__teamName">
                {currentMatch.awayTeam.internationalName}
              </span>
            </div>
          </div>

          <div className="betCard__footer">
            <div className="betCard__footerLeft">
              {currentMatch.wasPlayed && (
                <Typography className="betCard__result">
                  Result: {currentMatch.homeScoreRegular}-
                  {currentMatch.awayScoreRegular}
                </Typography>
              )}

              {isBettable && (
                <span className="betCard__hint">
                  {isGroupStage
                    ? `Exact ${POINTS_EXACT} · Outcome ${POINTS_OUTCOME} pts`
                    : "Knockout · boosted points"}
                </span>
              )}
            </div>

            <div className="betCard__footerRight">
              {isBettable ? (
                <Button
                  onClick={() => handleClick(bet)}
                  className="betCard__betButton"
                >
                  Bet
                </Button>
              ) : isLocked ? (
                <span className="betCard__locked">
                  <LockRoundedIcon />
                  You did not place a bet before kickoff.
                </span>
              ) : matchAlreadyPlayed && !currentMatch.wasPlayed ? (
                <span className="betCard__locked">
                  <LockRoundedIcon />
                  Match already played
                </span>
              ) : (
                <ShowPoints
                  betNumberOfPoints={bet.points}
                  betOutcome={bet.betResult}
                />
              )}
            </div>
          </div>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default BetCard;

interface ShowPointsProps {
  betOutcome: number;
  betNumberOfPoints: number;
}

export const ShowPoints: React.FC<ShowPointsProps> = ({
  betOutcome,
  betNumberOfPoints,
}) => {
  return (
    <div
      className="betCard__points"
      style={{
        color:
          betOutcome === 0
            ? palette.betWrong
            : betOutcome === 1
            ? palette.betCorrect
            : palette.betPerfect,
      }}
    >
      <BetOutcomeIcon outcome={betOutcome} />
      <span className="betCard__pointsText">
        {betOutcomeText(betOutcome)} · {betNumberOfPoints} pts
      </span>
    </div>
  );
};
